import {
  Flex,
  Avatar,
  Text,
  Box,
  Tabs,
  TabList,
  TabPanels,
  Alert,
  AlertTitle,
  AlertDescription,
  Icon,
  Spinner,
} from '@chakra-ui/react';
import { useState, useRef, useEffect } from 'react';
import { capitalizeFirstLetter } from '@/utils/commonHelpers';
import { useProjectActionConfig } from './useProjectActionConfig';
// import { BulkError } from '@/components/bulk-action/Error';
import { FormBuilder } from '@/components/formBuilder';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  getProjectDetails,
  projectDetailsActions,
  projectDetailsApiActions,
} from '@/store/projectDetails';
import { CustomTabWithStep } from '@/components/ats/Tabs';
import { PostModalForm } from './PostModalForm';
import { checkIfAnyFieldTrue } from '@/utils/schedule-interview-helpers';
import { Drawer } from '@/components/ats';
import { FeaturedIconWarning } from '@/assets/icons/orgIntegrations';
import { AppReview } from '@/assets/icons/ats/empty';
import { EmptyResults } from '@/components/ats';
import { KText } from '@/ui-library/components/text';
import { useShowToast } from '@/hooks/useShowToast';
import { useBulkOperation } from '@/components/ats/BulkOperationQueue';
import { useQueryClient } from '@tanstack/react-query';
import { PROJECT_PROSPECTS_QUERY_KEY } from '@/api/useGetProjectProspects';

export const SendForReviewV1 = ({
  modalActions,
  action = '',
  selected = [],
  toggleRowSelected = () => {},
  toggleAllRowsSelected = () => {},
  inputText = '',
  requestReviewFormConfig,
  selectedReviewerRef,
  // Only true for the checkbox bulk-action bar; single-row reviews stay sync.
  isBulkActivity = false,
}) => {
  const actionConfig = useProjectActionConfig(action, selected);

  const { statusFilter } = useSelector(getProjectDetails);

  const detailsRef = useRef(null);
  const postModalRef = useRef(null);

  const { onClose } = modalActions;
  const [isActionLoadingState, setActionProgress] = useState(false);

  const [, setErrorIds] = useState([]);

  const [, setUserInput] = useState(inputText || '');
  const [reviewDetails, setReviewDetails] = useState();
  const [currentTab, setCurrentTab] = useState('req_review_details');
  // With ~100 selected candidates the drawer body (the candidate list + form) is
  // expensive to render synchronously and visibly delays the drawer opening.
  // Open the drawer with a spinner first, then mount the heavy body next frame.
  const [isContentReady, setIsContentReady] = useState(false);
  const idsArray = [];

  const { applyStatusFilter } = projectDetailsActions;
  const reviewSelected =
    action === 'review'
      ? selected.filter((item) => !item?.current_review)
      : selected;
  const [reviewSelectedUsers, setReviewSelectedUsers] =
    useState(reviewSelected);
  // Candidates already in an active review – excluded from the editable list, but
  // still sent in the bulk request so the backend reports them as "already in review".
  const inReviewSelected =
    action === 'review' ? selected.filter((item) => item?.current_review) : [];
  const isAnyoneInReview = inReviewSelected.length > 0;
  const dispatch = useDispatch();

  const { isActionLoading = false, apiConfig = {} } = actionConfig;
  const { projectId } = useParams();
  const toast = useShowToast();
  const queryClient = useQueryClient();
  const reviewBulkOp = useBulkOperation({
    activityType: 'project_send_review',
    title: 'Sending candidates for review',
  });

  useEffect(() => {
    setActionProgress(isActionLoading);
  }, [isActionLoading]);

  useEffect(() => {
    // Defer the heavy body render to the next frame so the drawer + spinner
    // paint immediately on open instead of waiting for the full content.
    const raf = requestAnimationFrame(() => setIsContentReady(true));
    return () => cancelAnimationFrame(raf);
  }, []);

  const showToast = ({ title = '', description = '', status = 'info' }) =>
    toast({
      title: title,
      description: description,
      position: 'bottom-left',
      status: status,
      duration: 4000,
      isClosable: true,
    });

  const fetchProjectProspects = () => {
    const payload = { id: statusFilter?.id || statusFilter?.internalDefault };
    dispatch(applyStatusFilter(payload));
    // Prospects list lives in TanStack Query now — invalidate so it refetches.
    queryClient.invalidateQueries({ queryKey: [PROJECT_PROSPECTS_QUERY_KEY] });
  };

  const handleOnClose = (response = {}) => {
    const errorLength = response?.errors?.length || 1;
    onClose();
    setUserInput('');
    if (errorLength !== reviewSelectedUsers?.length) {
      fetchProjectProspects();
    }
  };

  const setInitialValues = () => {
    if (!detailsRef?.current?.values || !detailsRef || !reviewDetails) {
      return {
        requester_message: `Hi,\nPlease review these profiles to see if they would be a good fit for the role.`,
      };
    } else {
      return {
        requester_message: detailsRef?.current?.values?.requester_message,
        user_id: reviewDetails?.user_id,
        reviewer_id: reviewDetails?.reviewer_id,
      };
    }
  };

  const handleModalOnClose = () => {
    toggleAllRowsSelected(false);
    modalActions?.onClose();
  };

  const extendedModalActions = {
    ...modalActions,
    onClose: handleModalOnClose,
  };

  const renderAfterContents = () => {
    return (
      <>
        {['status', 'remove', 'review', 'add'].includes(action) && (
          <>
            <Box m="12px 0">
              {reviewSelectedUsers?.length > 0 && (
                <KText p="5px 0" size="sm-bold" color="secondary-900">
                  Candidates to {capitalizeFirstLetter(action)}{' '}
                </KText>
              )}
              {isAnyoneInReview && reviewSelectedUsers?.length > 0 && (
                <Alert
                  status="warning"
                  bg="#FFF2CC"
                  borderRadius="6px"
                  border={'1px solid #EAEAEA'}
                  mb={'lg'}
                  mt={'xs'}
                >
                  <Flex gap="8px">
                    <Icon
                      as={FeaturedIconWarning}
                      width="38px"
                      height="38px"
                      alignSelf="flex-start"
                    ></Icon>
                    <Flex>
                      <Box>
                        <AlertTitle fontSize={'14px'} fontWeight={700}>
                          {inReviewSelected.length} candidate
                          {inReviewSelected.length > 1 ? 's' : ''} already in
                          review{' '}
                        </AlertTitle>
                        <AlertDescription
                          display="block"
                          fontSize={'12px'}
                          fontWeight={400}
                          color="#454647"
                          paddingInlineEnd={'8px'}
                        >
                          The list below excludes them — they won&apos;t be sent
                          for review again.
                        </AlertDescription>
                      </Box>
                    </Flex>
                  </Flex>
                </Alert>
              )}
              {reviewSelectedUsers?.length > 0 && (
                <Box
                  mt="5px"
                  maxH="250px"
                  overflow="auto"
                  gap="5px"
                  border="2px solid"
                  borderColor={'ats-border-color'}
                  borderRadius={'sm'}
                  py={'10px'}
                  px={'15px'}
                >
                  {reviewSelectedUsers.map(renderUserList)}
                </Box>
              )}
              {reviewSelectedUsers?.length === 0 && (
                <EmptyResults
                  wrapperProps={{ h: '310px' }}
                  text="No candidate selected for review"
                  icon={AppReview}
                  iconProps={{ w: '160px', h: '120px', mb: '10px' }}
                  subText="LOOKS EMPTY IN HERE"
                />
              )}
            </Box>
          </>
        )}
      </>
    );
  };

  const handleRemoveUser = (tableId, currentUser) => {
    if (reviewSelectedUsers?.length > 1) {
      toggleRowSelected(tableId, false);
    }
    const UpdatedReviewSelectedUsers = reviewSelectedUsers.filter(
      (selectedUser) => selectedUser?.id !== currentUser?.id
    );
    setReviewSelectedUsers(UpdatedReviewSelectedUsers);
    setReviewDetails(detailsRef?.current?.values);
  };

  const renderUserList = (currentUser) => {
    const { contact, tableId } = currentUser || {};
    const { name, id, avatar, email } = contact || {};
    if (currentUser?.id) idsArray.push(currentUser?.id);

    return (
      <Flex key={id} padding="6px " alignItems="center">
        <Avatar
          src={avatar}
          name={name}
          width="32px"
          height="32px"
          borderRadius="12px"
        />
        <Flex flexDir="column" ml="12px" flex="1">
          <KText size="sm-bold">{name}</KText>
          {email && (
            <KText size="sm-bold" color="secondary-400">
              {email}
            </KText>
          )}
          {!email && (
            <KText size="sm-bold" color="status-1">
              No email
            </KText>
          )}
        </Flex>
        {reviewSelectedUsers?.length > 1 && (
          <Flex
            onClick={() => {
              handleRemoveUser(tableId, currentUser);
            }}
            cursor="pointer"
            title={`Clear ${name} from selection`}
          >
            <Text fontWeight="600" mr="3px" textAlign="center" fontSize="12px">
              Remove
            </Text>
          </Flex>
        )}
      </Flex>
    );
  };

  // const renderError = () => {
  //   if (errorIds?.length === 0) return null;
  //   return <BulkError contacts={errorIds} />;
  // };

  const requestReviewtabs = [
    {
      id: 'req_review_details',
      label: 'Details',
      props: { formRef: detailsRef },
    },
    {
      id: 'postApproval',
      label: 'Post-approval',
      component: PostModalForm,
      props: { formRef: postModalRef, currentTab: currentTab },
    },
  ];

  const getNextTab = () => {
    const currentIndex = requestReviewtabs.findIndex(
      (tab) => tab.id === currentTab
    );
    return requestReviewtabs?.[currentIndex + 1];
  };

  const submitFormsAndGetErrors = async () => {
    await detailsRef?.current?.submitForm();
    await postModalRef?.current?.submitForm();
    return {
      details: !detailsRef?.current?.isValid,
    };
  };

  const clickHandler = async () => {
    const errors = await submitFormsAndGetErrors();
    const res = checkIfAnyFieldTrue(errors);
    if (res || reviewSelectedUsers.length === 0) {
      return;
    } else {
      if (currentTab === 'req_review_details') {
        setCurrentTab('postApproval');
      }
      const detailsValues = detailsRef?.current?.values;
      const postModalValues = postModalRef?.current?.values;
      const updatedReviewDetails = {
        ...detailsValues,
        prospect_ids: idsArray || [],
      };
      setReviewDetails(updatedReviewDetails);
      const formattedDetailsValues = {
        prospect_ids: updatedReviewDetails?.prospect_ids || [],
        requester_message: updatedReviewDetails?.requester_message,
        user_id: selectedReviewerRef?.current?.id,
      };
      const params = {
        ...formattedDetailsValues,
        ...postModalValues,
      };
      if (currentTab === 'postApproval') {
        makeAPICall(params);
      }
    }
  };

  const makeAPICall = async (params) => {
    const config = { ...params };
    let updatedConfig = config;
    if (!apiConfig) return;
    const { urlPath, apiAction, contactField } = apiConfig;
    const urlPaths = { projectId };
    let requestConfig = {};
    urlPaths[urlPath] = urlPath;
    requestConfig[contactField] = reviewSelectedUsers.map((item) =>
      action === 'review' ? item?.id : item?.contact?.id
    );
    const isUnverifiedUser =
      typeof config?.user_id === 'string' && config?.user_id?.includes('--*--');
    if (action === 'review') {
      if (isUnverifiedUser) {
        const [userId, currentFormKey] = config.user_id.split('--*--');
        const intValue = parseInt(userId, 10);
        updatedConfig[currentFormKey] = intValue;
        delete requestConfig.user_id;
      }
      updatedConfig = config;
    }
    requestConfig = { ...requestConfig, ...updatedConfig };
    if (isUnverifiedUser) {
      delete requestConfig.user_id;
    }

    // Only checkbox bulk actions go through the async bulk-activity queue.
    // A single-row "Ask for review" stays on the synchronous path.
    const total = reviewSelectedUsers?.length || 0;
    if (isBulkActivity) {
      requestConfig.is_bulk_activity = true;
      // Include the already-in-review prospects so the backend records them as
      // skipped ("already in review") instead of silently dropping them. They
      // then surface as a skipped group in Recent activities.
      if (action === 'review' && inReviewSelected.length > 0) {
        requestConfig[contactField] = [
          ...requestConfig[contactField],
          ...inReviewSelected.map((item) => item?.id),
        ];
      }
      reviewBulkOp.startTracking(total);
    }
    const response = await dispatch(
      projectDetailsApiActions(apiAction, {
        urlPaths,
        requestConfig,
        isApi: true,
      })
    );
    if (isBulkActivity) {
      if (response?.errors) {
        reviewBulkOp.completeFailed();
      } else {
        reviewBulkOp.completeSuccess(total);
      }
    }
    handleAPIResponse(response);
  };

  const handleAPIResponse = (response) => {
    if (response?.errors?.reviews?.length > 0) {
      const errorMsg = response?.errors?.reviews?.map((error) => {
        const currentProspect = reviewSelectedUsers?.find(
          (item) => item?.id === error?.prospect_id
        );
        return {
          ...error,
          id: currentProspect?.contact?.id,
          message: error?.errors?.base[0],
        };
      });
      setErrorIds(errorMsg);
      return;
    }
    const alreadyPresentError =
      response?.errors?.project_collaborator?.errors?.account_invitation;
    if (alreadyPresentError?.length > 0) {
      const { email, errors } = alreadyPresentError[0];
      if (email || errors) {
        showToast({
          title: `${email} - ${errors?.email[0]}`,
          status: 'error',
        });
      } else if (alreadyPresentError[0] === 'err_invitation_already_exists') {
        showToast({
          title: `Invitation already exists for this email. Please wait until the user signs up.`,
          status: 'error',
        });
      }
      if (alreadyPresentError?.length !== reviewSelectedUsers?.length) {
        fetchProjectProspects();
      }
      return;
    }
    handleErrOrSuccess(response);
  };

  const handleErrOrSuccess = (response) => {
    if (response?.errors?.user_id?.length > 0) {
      const {
        errors: { user_id },
      } = response;
      showToast({ title: `user ${user_id[0]}`, status: 'error' });
      if (response?.errors?.user_id?.length !== reviewSelectedUsers?.length) {
        fetchProjectProspects();
      }
      return;
    }
    if (response?.errors?.length > 0) {
      setErrorIds(response?.errors);
      const { errors } = response;
      showToast({ title: `${errors[0]}`, status: 'error' });
      return;
    }
    if (response?.errors?.email?.length > 0) {
      showToast({
        title: `Email ${response?.errors?.email[0]}`,
        status: 'error',
      });
      return;
    }
    // Bulk activity is routed through the async bulk-operation queue, which
    // surfaces its own progress/success UI — skip the duplicate success toast.
    fetchProjectProspects();
    dispatch(
      projectDetailsApiActions('fetchProjectById', {
        urlPaths: { projectId },
      })
    );
    handleOnClose(response);
  };

  const getTabContentStyle = (currentTab, tabId) => ({
    px: 'xxl',
    py: '12px',
    minH: '100%',
    flex: '1',
    overflowY: 'scroll',
    style: {
      display: currentTab === tabId ? 'block' : 'none',
    },
  });

  return (
    <>
      <Drawer
        px={'32px'}
        py={'xxl'}
        drawerActions={extendedModalActions}
        onDrawerSubmit={clickHandler}
        ctaProps={{
          isDisabled:
            currentTab === 'req_review_details'
              ? reviewSelectedUsers.length === 0 ||
                !selectedReviewerRef?.current?.id
              : false,
        }}
        headerText="Request review"
        headerDescription="Your teammate will receive an email with a link to review the candidate’s profile"
        ctaLabel={
          currentTab !== 'postApproval'
            ? `Next: ${getNextTab()?.label} `
            : 'Send for review'
        }
        hasFooterDivider={true}
        isCtaLoading={
          currentTab === 'postApproval' ? isActionLoadingState : null
        }
      >
        {!isContentReady && (
          <Flex
            flex="1"
            alignItems="center"
            justifyContent="center"
            minH="320px"
            w="100%"
          >
            <Spinner
              size="lg"
              thickness="3px"
              color="primary-500"
              speed="0.6s"
            />
          </Flex>
        )}
        {isContentReady && (
          <Tabs
            display="flex"
            flexDir="column"
            flex="1"
            h="100%"
            alignItems="stretch"
            index={requestReviewtabs?.findIndex(
              (option) => option?.id === currentTab
            )}
            isLazy
          >
            <TabList
              sx={{
                borderTop: '1px solid',
                borderTopColor: 'ats-border-color',
                borderBottom: '1px solid',
                borderBottomColor: 'ats-border-color',
              }}
              background={'ats-tertiary'}
              p="sm"
            >
              {requestReviewtabs.map((option, idx) => {
                return (
                  <CustomTabWithStep
                    key={option.id}
                    label={option?.label}
                    step={idx + 1}
                    tabProps={idx === 0 ? { p: '0px -6px' } : { p: '0px 8px' }}
                    arrow={option?.id !== 'postApproval'}
                    onClick={async () => {
                      await submitFormsAndGetErrors();
                      if (
                        !detailsRef?.current?.isValid ||
                        reviewSelectedUsers.length === 0
                      ) {
                        return;
                      } else {
                        setReviewDetails(detailsRef?.current?.values);
                        setCurrentTab(option?.id);
                      }
                    }}
                    showError={
                      reviewSelectedUsers?.length === 0 &&
                      option?.id !== 'postApproval'
                    }
                    labelStyles={{
                      position: 'relative',
                      left: '4px',
                    }}
                    defaultTabSelectedStyles
                  />
                );
              })}
            </TabList>
            <TabPanels
              {...getTabContentStyle(currentTab, 'req_review_details')}
            >
              <>
                <FormBuilder
                  config={requestReviewFormConfig}
                  formRef={detailsRef}
                  initialValues={setInitialValues()}
                  onSubmit={getNextTab}
                />
                {renderAfterContents()}
              </>
            </TabPanels>
            <TabPanels {...getTabContentStyle(currentTab, 'postApproval')}>
              <PostModalForm formRef={postModalRef} />
            </TabPanels>
          </Tabs>
        )}
      </Drawer>
    </>
  );
};
